export type AccessTokenClaims = {
  /** Root-level `cidpId` claim; empty string when absent or not a non-empty string. */
  cidpId: string;
  /** Root-level `partnerId` claim as found; empty string when absent or not a string. */
  partnerId: string;
};

const EMPTY_CLAIMS: AccessTokenClaims = { cidpId: "", partnerId: "" };

const base64UrlDecode = (segment: string): string => {
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = window.atob(padded);
  // UTF-8 safe: turn the binary string into percent-encoded bytes first.
  return decodeURIComponent(
    binary
      .split("")
      .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join("")
  );
};

const asString = (value: unknown): string =>
  typeof value === "string" ? value : "";

/**
 * Reads `cidpId` and `partnerId` from the root of an access token payload
 * (second JWT segment, base64url). The signature is not verified: the backend
 * validates the token. Never throws; an undecodable token yields empty claims.
 */
export const decodeAccessTokenClaims = (
  token: string | null | undefined
): AccessTokenClaims => {
  try {
    if (typeof token !== "string") {
      return { ...EMPTY_CLAIMS };
    }

    const segments = token.split(".");
    if (segments.length < 2 || !segments[1]) {
      return { ...EMPTY_CLAIMS };
    }

    const payload = JSON.parse(base64UrlDecode(segments[1]));
    if (
      payload === null ||
      typeof payload !== "object" ||
      Array.isArray(payload)
    ) {
      return { ...EMPTY_CLAIMS };
    }

    return {
      cidpId: asString(payload.cidpId),
      partnerId: asString(payload.partnerId),
    };
  } catch (e) {
    return { ...EMPTY_CLAIMS };
  }
};
